/**
 * Live unit/total price preview from ACO quantity discount table.
 * ACO still applies the real discount in cart; this only previews on the PDP.
 */
(function () {
  if (window.__wod3dQtyDiscountPreview) return;
  window.__wod3dQtyDiscountPreview = true;

  function parseMoney(text) {
    const cleaned = String(text || '')
      .replace(/[^\d,.-]/g, '')
      .trim();
    if (!cleaned) return null;
    if (cleaned.includes(',') && cleaned.includes('.')) {
      return Number(cleaned.replace(/\./g, '').replace(',', '.'));
    }
    if (cleaned.includes(',')) return Number(cleaned.replace(',', '.'));
    return Number(cleaned);
  }

  function formatMoney(amount) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (_) {
      return amount.toFixed(2).replace('.', ',') + ' €';
    }
  }

  function parseTiers(table) {
    const tiers = [];
    table.querySelectorAll('tr').forEach((tr, index) => {
      if (index === 0) return;
      const cells = tr.querySelectorAll('td');
      if (cells.length < 2) return;
      const rangeText = cells[0].textContent.trim();
      const unit = parseMoney(cells[cells.length - 1].textContent);
      if (unit == null || Number.isNaN(unit)) return;

      let min = null;
      let max = Infinity;
      const range = rangeText.match(/(\d+)\s*[-–]\s*(\d+)/);
      const plusLeading = rangeText.match(/^\+\s*(\d+)/);
      const plus = rangeText.match(/(\d+)\s*\+/);
      if (range) {
        min = Number(range[1]);
        max = Number(range[2]);
      } else if (plusLeading) {
        min = Number(plusLeading[1]);
      } else if (plus) {
        min = Number(plus[1]);
      } else {
        return;
      }

      tiers.push({ min, max, unit, row: tr });
    });
    return tiers.sort((a, b) => a.min - b.min);
  }

  function findQtyInput(root) {
    return (
      root.querySelector('.wod-buy-qty .quantity__input') ||
      root.querySelector('product-form .quantity__input') ||
      root.querySelector('.product__info-container .quantity__input') ||
      document.querySelector('.product__info-container .quantity__input')
    );
  }

  function findPriceRoot(root) {
    return (
      root.querySelector('.product__info-container .price') ||
      document.querySelector('.product__info-container .price') ||
      document.querySelector('product-info .price')
    );
  }

  function removePriceRowNote(priceRoot) {
    const row = priceRoot?.closest('.product__price-row') || priceRoot?.parentElement;
    row?.querySelectorAll('.wod3d-qty-price-note:not(.wod3d-qty-price-note--buy)').forEach((el) => el.remove());
    // Also clear any note inserted as sibling of the price row (legacy placement).
    const after = row?.nextElementSibling;
    if (after?.classList?.contains('wod3d-qty-price-note') && !after.classList.contains('wod3d-qty-price-note--buy')) {
      after.remove();
    }
  }

  function ensureBuyNote(qtyInput) {
    const form =
      qtyInput.closest('form') ||
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('.wod-buy-form');
    if (!form) return null;

    let note = form.querySelector('.wod3d-qty-price-note--buy');
    if (note) return note;

    note = document.createElement('div');
    note.className = 'wod3d-qty-price-note wod3d-qty-price-note--buy';
    note.hidden = true;

    const qtyWrap =
      qtyInput.closest('.wod-buy-qty') ||
      qtyInput.closest('.product-form__quantity') ||
      qtyInput.closest('quantity-input');
    const submit = form.querySelector('.product-form__submit');

    // Prefer above the quantity selector (still above the ATC button).
    if (qtyWrap) {
      qtyWrap.insertAdjacentElement('beforebegin', note);
    } else if (submit) {
      submit.insertAdjacentElement('beforebegin', note);
    } else {
      form.appendChild(note);
    }
    return note;
  }

  function getBasePrice(priceRoot) {
    if (priceRoot?.dataset.wod3dBasePrice) {
      return Number(priceRoot.dataset.wod3dBasePrice);
    }
    const sale = priceRoot?.querySelector('.price-item--sale');
    const regular = priceRoot?.querySelector('.price-item--regular');
    const amount = parseMoney((sale || regular)?.textContent);
    if (amount != null && !Number.isNaN(amount)) {
      priceRoot.dataset.wod3dBasePrice = String(amount);
      priceRoot.dataset.wod3dBaseHtml = priceRoot.innerHTML;
    }
    return amount;
  }

  function setPriceDisplay(priceRoot, unitPrice, basePrice, discounted) {
    if (!priceRoot) return;
    if (!discounted) {
      if (priceRoot.dataset.wod3dBaseHtml) {
        priceRoot.innerHTML = priceRoot.dataset.wod3dBaseHtml;
      }
      // Important: class lives on the root; restoring HTML alone leaves "Oferta" visible.
      priceRoot.classList.remove('price--on-sale', 'volume-pricing--sale-badge');
      priceRoot.querySelectorAll('.price__badge-sale').forEach((el) => {
        el.setAttribute('hidden', '');
        el.style.display = 'none';
      });
      // Never surface "Agotado" from restored markup unless the variant is actually sold out.
      if (!priceRoot.classList.contains('price--sold-out')) {
        priceRoot.querySelectorAll('.price__badge-sold-out').forEach((el) => {
          el.setAttribute('hidden', '');
          el.style.display = 'none';
          el.remove();
        });
      }
      return;
    }

    // Sale price first so it stays left-aligned like the base PDP price; compare-at after.
    // Keep `price--show-badge`: theme sizes `.price.price--large.price--show-badge` at 4rem.
    // Do not inject badges — volume savings are shown in chips/notes instead.
    priceRoot.innerHTML =
      `<div class="price__container">` +
      `<div class="price__regular">` +
      `<span class="visually-hidden">${t('qtyRegularPrice', 'Precio habitual')}</span>` +
      `<span class="price-item price-item--regular">${formatMoney(basePrice)}</span>` +
      `</div>` +
      `<div class="price__sale">` +
      `<span class="visually-hidden">${t('qtySalePrice', 'Precio de oferta')}</span>` +
      `<span class="price-item price-item--sale price-item--last">${formatMoney(unitPrice)}</span>` +
      `<span class="visually-hidden">${t('qtyRegularPrice', 'Precio habitual')}</span>` +
      `<span><s class="price-item price-item--regular">${formatMoney(basePrice)}</s></span>` +
      `</div>` +
      `</div>`;
    priceRoot.classList.add('price--on-sale');
    priceRoot.classList.remove('volume-pricing--sale-badge');
  }

  function t(key, fallback, vars) {
    let out = (window.wod3dI18n && window.wod3dI18n[key]) || fallback || '';
    if (vars) {
      Object.keys(vars).forEach((k) => {
        out = String(out).split('{{ ' + k + ' }}').join(String(vars[k] ?? ''));
        out = String(out).split('{{' + k + '}}').join(String(vars[k] ?? ''));
      });
    }
    return out;
  }

  function noteHtml(unit, total, basePrice, qty) {
    const savedPerUnit = Math.max(0, basePrice - unit);
    const savedTotal = savedPerUnit * qty;
    return (
      `<span class="wod3d-qty-price-note__main">` +
      `<strong>${formatMoney(unit)}</strong>/${t('qtyEach', 'ud.')}` +
      `<span class="wod3d-qty-price-note__sep">·</span>` +
      `Total <strong>${formatMoney(total)}</strong>` +
      `</span>` +
      (savedTotal > 0
        ? `<span class="wod3d-qty-price-note__save">${t('qtySave', 'Ahorras {{ amount }}', {
            amount: formatMoney(savedTotal),
          })}</span>`
        : '') +
      `<span class="wod3d-qty-price-note__hint">${t('qtyConfirmInCart', 'Se confirma en el carrito')}</span>`
    );
  }

  function setNotes(notes, html, visible) {
    notes.forEach((note) => {
      if (!note) return;
      note.hidden = !visible;
      note.innerHTML = visible ? html : '';
    });
  }

  function hideDiscountColumns(table) {
    const headerRow = table.querySelector('tr');
    if (!headerRow) return;
    const colCount = headerRow.children.length;
    if (colCount < 1) return;

    table.querySelectorAll('tr').forEach((tr) => {
      Array.from(tr.children).forEach((cell, index) => {
        cell.classList.remove('wod3d-qty-col--qty', 'wod3d-qty-col--price', 'wod3d-qty-col--discount');
        if (index === 0) {
          cell.classList.add('wod3d-qty-col--qty');
          return;
        }
        if (index === colCount - 1) {
          cell.classList.add('wod3d-qty-col--price');
          return;
        }
        // Middle columns = discount amount/% — hide to avoid confusion
        cell.classList.add('wod3d-qty-col--discount');
        cell.setAttribute('hidden', '');
      });
    });
  }

  function formatLastTierQtyLabel(table) {
    const rows = Array.from(table.querySelectorAll('tr')).filter((tr, index) => {
      if (index === 0) return false;
      return Boolean(tr.querySelector('td'));
    });
    const last = rows[rows.length - 1];
    if (!last) return;

    const qtyCell = last.querySelector('td.wod3d-qty-col--qty') || last.querySelector('td');
    if (!qtyCell || qtyCell.dataset.wod3dPlusLabel === 'true') return;

    const text = qtyCell.textContent.trim();
    const range = text.match(/(\d+)\s*[-–]\s*(\d+)/);
    const plusLeading = text.match(/^\+\s*(\d+)/);
    const plus = text.match(/(\d+)\s*\+/);
    const min = range ? Number(range[1]) : plusLeading ? Number(plusLeading[1]) : plus ? Number(plus[1]) : null;
    if (min == null || Number.isNaN(min)) return;

    qtyCell.dataset.wod3dPlusLabel = 'true';
    qtyCell.textContent = `+${min}`;
  }

  function ensureTableHeading(table) {
    if (!table || !table.parentNode) return;

    let panel = table.closest('.wod3d-qty-table-panel');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'wod3d-qty-table-panel';
      table.parentNode.insertBefore(panel, table);
      panel.appendChild(table);
    }

    if (table.dataset.wod3dHeadingBound === 'true') return;
    table.dataset.wod3dHeadingBound = 'true';

    if (panel.querySelector(':scope > .wod3d-qty-table-heading')) return;

    const heading = document.createElement('p');
    heading.className = 'wod3d-qty-table-heading';
    heading.innerHTML =
      '<span class="wod3d-qty-table-heading__icon" aria-hidden="true">🔥</span>' +
      `<span class="wod3d-qty-table-heading__text">${t(
        'qtyVolumeHook',
        'Cuanto más encargues, mejor precio por unidad'
      )}</span>`;
    panel.insertBefore(heading, table);
  }

  function setupRowLayout(table) {
    if (!table || table.dataset.wod3dRowBound === 'true') return;
    table.dataset.wod3dRowBound = 'true';
    table.classList.add('wod3d-qty-table--row');
    hideDiscountColumns(table);
    formatLastTierQtyLabel(table);
    ensureTableHeading(table);
  }

  function setupTableCollapse(table) {
    // Compact horizontal chips already take little space — no collapse UI.
    if (!table || table.dataset.wod3dCollapseBound === 'true') return null;
    table.dataset.wod3dCollapseBound = 'true';
    return null;
  }

  function mount(root) {
    const scope = root || document;
    const table = scope.querySelector('.acocd-quantity_table') || document.querySelector('.acocd-quantity_table');
    if (!table) return;

    setupRowLayout(table);
    const collapse = setupTableCollapse(table);
    if (table.dataset.wod3dPreviewBound === 'true') return;

    const tiers = parseTiers(table);
    if (!tiers.length) return;

    const qtyInput = findQtyInput(scope);
    const priceRoot = findPriceRoot(scope);
    if (!qtyInput || !priceRoot) return;

    table.dataset.wod3dPreviewBound = 'true';
    const basePrice = getBasePrice(priceRoot);
    if (basePrice == null || Number.isNaN(basePrice)) return;

    removePriceRowNote(priceRoot);
    const buyNote = ensureBuyNote(qtyInput);

    function tierForQty(qty) {
      return tiers.find((t) => qty >= t.min && qty <= t.max) || null;
    }

    function render() {
      const qty = Math.max(1, Number(qtyInput.value) || 1);
      const tier = tierForQty(qty);

      tiers.forEach((t) => t.row.classList.toggle('is-active-tier', t === tier));

      if (tier?.row?.classList.contains('wod3d-tier-row--extra') && collapse) {
        collapse.setExpanded(true);
      }

      if (!tier) {
        setPriceDisplay(priceRoot, basePrice, basePrice, false);
        setNotes([buyNote], '', false);
        return;
      }

      const unit = tier.unit;
      const total = unit * qty;
      setPriceDisplay(priceRoot, unit, basePrice, true);
      setNotes([buyNote], noteHtml(unit, total, basePrice, qty), true);
    }

    qtyInput.addEventListener('input', render);
    qtyInput.addEventListener('change', render);

    // quantity-input +/- buttons
    qtyInput.closest('quantity-input')?.addEventListener('change', render);

    table.querySelectorAll('tr').forEach((tr, index) => {
      if (index === 0) return;
      tr.classList.add('is-clickable-tier');
      tr.title = 'Usar este tramo de cantidad';
      tr.addEventListener('click', () => {
        const tier = tiers.find((t) => t.row === tr);
        if (!tier) return;
        qtyInput.value = String(tier.min);
        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    render();
  }

  function boot() {
    mount(document);
    // ACO can inject late
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      mount(document);
      if (
        (document.querySelector('.acocd-quantity_table[data-wod3d-preview-bound="true"]') &&
          document.querySelector('.acocd-quantity_table[data-wod3d-collapse-bound="true"]')) ||
        tries > 20
      ) {
        window.clearInterval(timer);
      }
    }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const table = event.target.querySelector?.('.acocd-quantity_table');
    if (table) {
      delete table.dataset.wod3dPreviewBound;
      delete table.dataset.wod3dCollapseBound;
      delete table.dataset.wod3dRowBound;
      delete table.dataset.wod3dHeadingBound;
      table.classList.remove('wod3d-qty-table--row');
      const panel = table.closest('.wod3d-qty-table-panel');
      if (panel) {
        panel.querySelectorAll(':scope > .wod3d-qty-table-heading').forEach((el) => el.remove());
        panel.parentNode?.insertBefore(table, panel);
        panel.remove();
      } else {
        table.previousElementSibling?.classList?.contains('wod3d-qty-table-heading') &&
          table.previousElementSibling.remove();
      }
      const shell = table.closest('.wod3d-qty-table-shell');
      shell?.parentElement?.querySelector(':scope > .wod3d-qty-table-toggle')?.remove();
      if (shell) {
        shell.parentNode.insertBefore(table, shell);
        shell.remove();
      }
    }
    mount(event.target);
  });
})();
